import { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { BlogPost, BlogPostInsert } from '@/types/blog';

interface PostFormProps {
  post: BlogPost | null;
  onSaved: () => void;
  onCancel: () => void;
}

const CATEGORIAS = ['Direito Penal', 'Penal Empresarial', 'Direito Civil', 'Compliance Criminal', 'Habeas Corpus', 'Geral'];

function generateSlug(titulo: string): string {
  return titulo
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export default function PostForm({ post, onSaved, onCancel }: PostFormProps) {
  const [titulo, setTitulo] = useState('');
  const [resumo, setResumo] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [categoria, setCategoria] = useState('Penal Empresarial');
  const [tempoLeitura, setTempoLeitura] = useState('5 min de leitura');
  const [publicado, setPublicado] = useState(false);
  const [imagemUrl, setImagemUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (post) {
      setTitulo(post.titulo);
      setResumo(post.resumo);
      setConteudo(post.conteudo);
      setCategoria(post.categoria);
      setTempoLeitura(post.tempo_leitura);
      setPublicado(post.publicado);
      setImagemUrl(post.imagem_url);
      setImagePreview(post.imagem_url);
    }
  }, [post]);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('A imagem deve ter no máximo 5MB.');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setError('');
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    setUploadingImage(true);
    const ext = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('blog-images')
      .upload(fileName, file, { upsert: false });

    setUploadingImage(false);

    if (uploadError) {
      setError('Erro ao fazer upload da imagem. Tente novamente.');
      return null;
    }

    const { data } = supabase.storage.from('blog-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!titulo.trim() || !resumo.trim() || !conteudo.trim()) {
      setError('Preencha todos os campos obrigatórios.');
      return;
    }

    setSaving(true);

    let finalImageUrl = imagemUrl;

    if (imageFile) {
      const uploaded = await uploadImage(imageFile);
      if (!uploaded) {
        setSaving(false);
        return;
      }
      finalImageUrl = uploaded;
    }

    const slug = post?.slug || generateSlug(titulo);

    const payload: BlogPostInsert = {
      slug,
      titulo: titulo.trim(),
      resumo: resumo.trim(),
      conteudo: conteudo.trim(),
      categoria,
      tempo_leitura: tempoLeitura.trim(),
      publicado,
      imagem_url: finalImageUrl,
      data_publicacao: post?.data_publicacao || new Date().toISOString(),
    };

    let dbError;

    if (post) {
      const { error } = await supabase
        .from('blog_posts')
        .update(payload)
        .eq('id', post.id);
      dbError = error;
    } else {
      const { error } = await supabase.from('blog_posts').insert(payload);
      dbError = error;
    }

    setSaving(false);

    if (dbError) {
      setError('Erro ao salvar o post. Tente novamente.');
      return;
    }

    onSaved();
  };

  const isLoading = saving || uploadingImage;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-400 text-sm"></i>
          <p className="text-red-600 font-sans text-xs">{error}</p>
        </div>
      )}

      {/* Título */}
      <div>
        <label className="block font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/50 mb-2">
          Título <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          required
          className="w-full border border-[#E9D59C]/60 rounded-md px-4 py-3 font-serif text-base text-[#0A1829] placeholder-[#0A1829]/25 focus:outline-none focus:border-[#B39B62] transition-colors bg-white"
          placeholder="Título do artigo..."
        />
      </div>

      {/* Resumo */}
      <div>
        <label className="block font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/50 mb-2">
          Resumo <span className="text-red-400">*</span>
        </label>
        <textarea
          value={resumo}
          onChange={(e) => setResumo(e.target.value)}
          required
          rows={3}
          maxLength={500}
          className="w-full border border-[#E9D59C]/60 rounded-md px-4 py-3 font-sans text-sm text-[#0A1829] placeholder-[#0A1829]/25 focus:outline-none focus:border-[#B39B62] transition-colors resize-none bg-white"
          placeholder="Breve resumo do artigo (aparece no card do blog)..."
        />
        <p className="font-sans text-xs text-[#0A1829]/35 mt-1">{resumo.length}/500 caracteres</p>
      </div>

      {/* Categoria + Tempo de Leitura */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/50 mb-2">
            Categoria
          </label>
          <select
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            className="w-full border border-[#E9D59C]/60 rounded-md px-4 py-3 font-sans text-sm text-[#0A1829] focus:outline-none focus:border-[#B39B62] transition-colors bg-white cursor-pointer"
          >
            {CATEGORIAS.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/50 mb-2">
            Tempo de Leitura
          </label>
          <input
            type="text"
            value={tempoLeitura}
            onChange={(e) => setTempoLeitura(e.target.value)}
            className="w-full border border-[#E9D59C]/60 rounded-md px-4 py-3 font-sans text-sm text-[#0A1829] placeholder-[#0A1829]/25 focus:outline-none focus:border-[#B39B62] transition-colors bg-white"
            placeholder="ex: 5 min de leitura"
          />
        </div>
      </div>

      {/* Imagem */}
      <div>
        <label className="block font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/50 mb-2">
          Imagem de Destaque
        </label>
        <div className="flex flex-col sm:flex-row gap-4">
          {imagePreview && (
            <div className="relative w-full sm:w-40 h-28 rounded-md overflow-hidden border border-[#E9D59C]/50 shrink-0">
              <img src={imagePreview} alt="Preview" className="w-full h-full object-cover object-top" />
              <button
                type="button"
                onClick={() => {
                  setImageFile(null);
                  setImagePreview(null);
                  setImagemUrl(null);
                }}
                className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full cursor-pointer hover:bg-red-600 transition-colors"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          )}
          <label className={`flex-1 border-2 border-dashed border-[#E9D59C]/60 hover:border-[#B39B62]/50 rounded-md px-6 py-8 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${imagePreview ? 'py-4' : 'py-8'}`}>
            <i className="ri-upload-cloud-line text-2xl text-[#B39B62]/60"></i>
            <span className="font-sans text-xs text-[#0A1829]/50">
              {imagePreview ? 'Trocar imagem' : 'Clique para fazer upload'}
            </span>
            <span className="font-sans text-xs text-[#0A1829]/30">PNG, JPG, WEBP • Máx. 5MB</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleImageChange}
            />
          </label>
        </div>
      </div>

      {/* Conteúdo */}
      <div>
        <label className="block font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/50 mb-2">
          Conteúdo <span className="text-red-400">*</span>
        </label>
        <div className="border border-[#E9D59C]/60 rounded-md overflow-hidden focus-within:border-[#B39B62] transition-colors">
          <div className="bg-[#F8F5EE]/80 border-b border-[#E9D59C]/40 px-4 py-2 flex flex-wrap gap-2">
            {[
              { label: 'H2', tag: '<h2>Título</h2>' },
              { label: 'H3', tag: '<h3>Subtítulo</h3>' },
              { label: 'P', tag: '<p>Parágrafo</p>' },
              { label: 'B', tag: '<strong>Negrito</strong>' },
              { label: 'UL', tag: '<ul>\n  <li>Item</li>\n</ul>' },
              { label: 'LI', tag: '<li>Item</li>' },
            ].map((btn) => (
              <button
                key={btn.label}
                type="button"
                onClick={() => setConteudo((prev) => prev + '\n' + btn.tag)}
                className="font-sans text-xs font-semibold text-[#0A1829]/50 hover:text-[#B39B62] border border-[#E9D59C]/60 hover:border-[#B39B62]/40 px-2.5 py-1 rounded cursor-pointer transition-colors whitespace-nowrap"
              >
                {btn.label}
              </button>
            ))}
          </div>
          <textarea
            value={conteudo}
            onChange={(e) => setConteudo(e.target.value)}
            required
            rows={16}
            className="w-full px-4 py-4 font-mono text-xs text-[#0A1829] placeholder-[#0A1829]/25 focus:outline-none resize-none bg-white"
            placeholder="Digite o conteúdo em HTML...&#10;&#10;Exemplo:&#10;<h2>Título da seção</h2>&#10;<p>Parágrafo do artigo.</p>"
          />
        </div>
        <p className="font-sans text-xs text-[#0A1829]/35 mt-1.5">Use HTML básico para formatação (h2, h3, p, strong, ul, li).</p>
      </div>

      {/* Status */}
      <div className="flex items-center gap-3 py-3 px-4 bg-[#F8F5EE] border border-[#E9D59C]/40 rounded-md">
        <button
          type="button"
          onClick={() => setPublicado(!publicado)}
          className={`relative w-10 h-5.5 rounded-full transition-all duration-300 cursor-pointer shrink-0 ${publicado ? 'bg-[#B39B62]' : 'bg-[#0A1829]/20'}`}
          style={{ height: '22px' }}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 ${publicado ? 'left-5.5' : 'left-0.5'}`}
            style={{ left: publicado ? '22px' : '2px' }}
          ></span>
        </button>
        <div>
          <p className="font-sans text-sm text-[#0A1829] font-medium">{publicado ? 'Publicado' : 'Rascunho'}</p>
          <p className="font-sans text-xs text-[#0A1829]/45">
            {publicado ? 'Visível publicamente no blog' : 'Salvo como rascunho, não aparece no blog'}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-[#E9D59C]/40">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="font-sans text-sm text-[#0A1829]/60 hover:text-[#0A1829] border border-[#0A1829]/15 hover:border-[#0A1829]/30 px-6 py-2.5 rounded-md transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center gap-2 bg-[#0A1829] hover:bg-[#0A1829]/85 text-white font-sans font-semibold text-sm px-6 py-2.5 rounded-md transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-sm"></i>
              {uploadingImage ? 'Enviando imagem...' : 'Salvando...'}
            </>
          ) : (
            <>
              <i className="ri-save-line text-sm"></i>
              {post ? 'Salvar alterações' : 'Criar post'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}
